import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, User } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const VideoTagger = ({ videoFile, friends, onTagsChange }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [tags, setTags] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoUrl, setVideoUrl] = useState(null);
  const [showFriendSelector, setShowFriendSelector] = useState(false);
  const [pendingTag, setPendingTag] = useState(null);

  useEffect(() => {
    if (videoFile) {
      const url = URL.createObjectURL(videoFile);
      setVideoUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [videoFile]);

  useEffect(() => {
    onTagsChange(tags);
  }, [tags, onTagsChange]);

  const handleVideoClick = (e) => {
    if (!containerRef.current || friends.length === 0) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setPendingTag({ x, y, timestamp: videoRef.current.currentTime });
    setShowFriendSelector(true);
  };

  const handleFriendSelect = (friendId) => {
    const friend = friends.find(f => f.id === friendId);
    if (friend && pendingTag) {
      const newTag = {
        friend_id: friend.id,
        friend_username: friend.username,
        x: pendingTag.x,
        y: pendingTag.y,
        timestamp: pendingTag.timestamp
      };
      setTags([...tags, newTag]);
      setShowFriendSelector(false);
      setPendingTag(null);
    }
  };

  const removeTag = (index) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const isTagVisible = (tag) => {
    // Show tag if we're within 3 seconds of the tagged timestamp
    return Math.abs(currentTime - tag.timestamp) < 3;
  };

  if (!videoUrl) return null;

  return (
    <div className="space-y-4">
      <div className="relative" ref={containerRef}>
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          className="w-full rounded-lg"
          onTimeUpdate={handleTimeUpdate}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onClick={handleVideoClick}
          style={{ cursor: friends.length > 0 ? 'crosshair' : 'default' }}
        />
        
        {/* Video Tags Overlay */}
        {tags.map((tag, index) => (
          isTagVisible(tag) && (
            <div
              key={index}
              className="absolute bg-purple-600/90 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2 shadow-lg animate-in fade-in zoom-in"
              style={{
                left: `${tag.x}%`,
                top: `${tag.y}%`,
                transform: 'translate(-50%, -50%)'
              }}
            >
              <User className="w-3 h-3" />
              {tag.friend_username}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(index);
                }}
                className="hover:bg-white/20 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )
        ))}

        {/* Pending Tag Position */}
        {showFriendSelector && pendingTag && (
          <div
            className="absolute w-8 h-8 border-2 border-purple-400 rounded-full animate-pulse"
            style={{
              left: `${pendingTag.x}%`,
              top: `${pendingTag.y}%`,
              transform: 'translate(-50%, -50%)'
            }}
          />
        )}
      </div>

      {/* Friend Selector */}
      {showFriendSelector && (
        <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
          <p className="text-sm text-slate-300 mb-2">Who is at this position?</p>
          <div className="flex gap-2">
            <Select onValueChange={handleFriendSelect}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                <SelectValue placeholder="Select friend" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {friends.map(friend => (
                  <SelectItem key={friend.id} value={friend.id} className="text-white">
                    {friend.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowFriendSelector(false);
                setPendingTag(null);
              }}
              className="border-slate-700 text-slate-300"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Tagged Friends List */}
      {tags.length > 0 && (
        <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
          <h4 className="text-sm font-semibold text-white mb-3">Tagged in Video:</h4>
          <div className="space-y-2">
            {tags.map((tag, index) => (
              <div key={index} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-slate-300">
                  <User className="w-4 h-4" />
                  <span className="font-medium">{tag.friend_username}</span>
                  <span className="text-slate-500">
                    @ {Math.floor(tag.timestamp)}s
                  </span>
                </div>
                <button
                  onClick={() => removeTag(index)}
                  className="text-red-400 hover:text-red-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {friends.length > 0 && (
        <p className="text-xs text-slate-400 text-center">
          Click on the video to tag friends at specific moments
        </p>
      )}
    </div>
  );
};

export default VideoTagger;
